import { always, has, ifElse, map, mergeRight, propEq } from 'ramda';
import { handleActions } from 'redux-actions';

import { EMPTY_VALUES } from 'constants/general';

import * as types from './actionTypes';
import { TABS } from './constants';

export const initialState = {
	activeTab: TABS.notificationSending,
	notificationFilters: EMPTY_VALUES.ARRAY,
	taskFilters: EMPTY_VALUES.ARRAY,
};

const setUserNotifications = (state, { payload }) => {
	const { notificationSettings } = payload;
	return {
		...state,
		notificationFilters: notificationSettings,
	};
};

const changeTab = (state, { payload }) => {
	const { tab } = payload;
	return {
		...state,
		activeTab: tab,
	};
};

const changeAllNotifs = (state, { payload }) => {
	const { notificationSetId, originalValue } = payload;
	const { notificationFilters = [] } = state;

	const filtersWithChosenId = notificationFilters.filter((item) => item.notificationSetId === notificationSetId);
	const otherFilters = notificationFilters.filter((item) => item.notificationSetId !== notificationSetId);
	const updated = filtersWithChosenId.map((item) => ({ ...item, enabled: !originalValue }));

	return {
		...state,
		notificationFilters: [...otherFilters, ...updated],
	};
};

const changeSubNotif = (state, { payload }) => {
	const { notificationSetId, taskTypeId } = payload;
	const { notificationFilters = [] } = state;

	const chosenSubNotif = notificationFilters.find(
		(item) => item.notificationSetId === notificationSetId && item.taskTypeId === taskTypeId,
	);

	const otherTaskTypeIdFilters = notificationFilters.filter((item) => item.taskTypeId !== taskTypeId);
	const otherSameTaskTypeIdDifferentNotifIdFilters = notificationFilters.filter(
		(item) => item.notificationSetId !== notificationSetId && item.taskTypeId === taskTypeId,
	);

	const updatedSubNotif = {
		...chosenSubNotif,
		enabled: !chosenSubNotif.enabled,
	};

	return {
		...state,
		notificationFilters: [
			...otherTaskTypeIdFilters,
			...otherSameTaskTypeIdDifferentNotifIdFilters,
			updatedSubNotif,
		],
	};
};

const setTaskFilters = (state, { payload }) => {
	const { taskFilters } = payload;

	return {
		...state,
		taskFilters,
	};
};

const changeSubfilterState = (state, { payload }) => {
	const { taskTypeId, filterId } = payload;

	const update = (filter) =>
		mergeRight(filter, {
			subTypes: map(
				ifElse(
					propEq(filterId, 'taskSubtypeId'),
					(subFilter) =>
						mergeRight(subFilter, {
							enabled: !subFilter.enabled,
						}),
					(item) => item,
				),
				filter.subTypes,
			),
		});

	return mergeRight(state, {
		taskFilters: map(
			ifElse(propEq(taskTypeId, 'taskTypeId'), update, (item) => item),
			state.taskFilters,
		),
	});
};

const changeFilterStateWithoutSubfilter = (state, action) => {
	const { taskTypeId } = action.payload;

	const update = (filter) =>
		mergeRight(filter, {
			enabled: taskTypeId === filter.taskTypeId ? !filter.enabled : filter.enabled,
		});

	return mergeRight(state, {
		taskFilters: map(update, state.taskFilters),
	});
};

const changeFilterState = (state, action) => {
	const { taskTypeId } = action.payload;

	const hasSubtype = (filter) =>
		mergeRight(filter, {
			enabled: taskTypeId === filter.taskTypeId ? !filter.enabled : filter.enabled,
			subTypes: map(
				(subFilter) =>
					mergeRight(subFilter, {
						enabled: taskTypeId === subFilter.taskTypeId ? !filter.enabled : subFilter.enabled,
					}),
				filter.subTypes,
			),
		});

	const hasNotSubtype = (filter) =>
		mergeRight(filter, {
			enabled: taskTypeId === filter.taskTypeId ? !filter.enabled : filter.enabled,
		});

	return mergeRight(state, {
		taskFilters: map(ifElse(has('subTypes'), hasSubtype, hasNotSubtype), state.taskFilters),
	});
};

const settingsReducer = {
	[types.CHANGE_TAB]: changeTab,
	[types.SET_USER_NOTIFS]: setUserNotifications,
	[types.SET_TASK_FILTERS]: setTaskFilters,
	[types.CHANGE_SUBFILTER_CHECKED]: changeSubfilterState,
	[types.CHANGE_FILTER_CHECKED_WITHOUT_SUBFILTER]: changeFilterStateWithoutSubfilter,
	[types.CHANGE_FILTER_CHECKED]: changeFilterState,
	[types.CHANGE_ALL_NOTIFS]: changeAllNotifs,
	[types.CHANGE_SUB_NOTIF]: changeSubNotif,
	[types.RESET_REDUCER]: always(initialState),
};

export default handleActions(settingsReducer, initialState);
